import { SupervisorAccount } from "@mui/icons-material";
import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { TableIconCell } from "components/TableIconCell";
import { equalsIgnoringCase, getFormattedDate } from "data/functions";
import {
  Account,
  SortableColumns,
  SortOrder,
  User,
  TableHeaderColumn,
  userTableHeaderColumns,
} from "data/interfaces";
import { useEffect, useState } from "react";
import { AccountStatusChip } from "components/AccountStatusChip";

interface UserTableProps {
  users: User[];
  selected: User[];
  account?: Account;
  onSelect: (selected: boolean, user: User) => void;
  onSelectAll: (allSelected: boolean) => void;
  orderBy: keyof SortableColumns;
  order: SortOrder;
  onSort: (column: string) => void;
  page: number;
  onPageChange: (page: number) => void;
  onSelectUser: (user: User) => void;
}

const UserTable = ({
  users,
  selected,
  account,
  onSelect,
  onSelectAll,
  orderBy,
  order,
  onSort,
  page,
  onPageChange,
  onSelectUser,
}: UserTableProps) => {
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const isSelected = (user: User) => selected?.includes(user);

  useEffect(() => {
    if (users.length <= rowsPerPage * page) {
      onPageChange(page - 1);
    }
  }, [users, rowsPerPage, page, onPageChange]);

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    onPageChange(0);
  };

  const userPrimaryAdmin = (user: User) => {
    return (
      account !== undefined && equalsIgnoringCase(account.admin, user.email)
    );
  };

  return (
    <Paper>
      <TableContainer>
        <Table size="small" sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={
                    selected.length > 0 && selected.length === users.length
                  }
                  indeterminate={
                    selected.length > 0 && selected.length < users.length
                  }
                  onChange={(e, checked) => onSelectAll(checked)}
                />
              </TableCell>
              <TableCell></TableCell>
              {userTableHeaderColumns.map((column: TableHeaderColumn) => (
                <TableCell key={column.id}>
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => onSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    <>{column.label}</>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {users
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((user) => (
                <TableRow
                  key={user.id ?? `unregistered-${user.email}`}
                  hover
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected(user)}
                      onChange={(e, checked) => onSelect(checked, user)}
                    />
                  </TableCell>
                  <TableIconCell
                    title={
                      userPrimaryAdmin(user)
                        ? "Primary Account Admin"
                        : "Account Admin"
                    }
                    onClick={() => onSelectUser({ ...user })}
                  >
                    {userPrimaryAdmin(user) ? (
                      <SupervisorAccount color="error" />
                    ) : (
                      user.isAdmin && <SupervisorAccount />
                    )}
                  </TableIconCell>
                  <TableCell onClick={() => onSelectUser({ ...user })}>
                    {user.name || "New user"}
                  </TableCell>
                  <TableCell onClick={() => onSelectUser({ ...user })}>
                    {user.displayEmail}
                  </TableCell>
                  <TableCell onClick={() => onSelectUser({ ...user })}>
                    <AccountStatusChip accountStatus={user.accountStatus} />
                  </TableCell>
                  <TableCell onClick={() => onSelectUser({ ...user })}>
                    {user.lastLogin && getFormattedDate(user.lastLogin)}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={users.length}
        page={users.length <= rowsPerPage * page ? page - 1 : page}
        onPageChange={(event: any, page: number) => {
          onPageChange(page);
        }}
        rowsPerPageOptions={[10, 20, 50]}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default UserTable;
